import type {
  CalendarFilters,
  CalendarSelection,
  CombinedCalendar,
  CombinedMonth,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The combined calendar's reads (STORY-195). **Clinic-wide routes**: the
 * locations are named in the query, and the active location still goes in the
 * header because the permission to see several at once is asked there (CF-D8).
 */

/** A day (`days: 1`) or a week (`days: 7`) of every named location, each on its own clock. */
export function getCombined(
  from: string,
  days: 1 | 7,
  branchIds: readonly string[],
  signal?: AbortSignal,
): Promise<CombinedCalendar> {
  const query = new URLSearchParams({ from, days: String(days), branchIds: branchIds.join(",") });
  return api<CombinedCalendar>(`/calendar/combined?${query.toString()}`, { signal });
}

/** A month, counted on the server with the filters applied (a month is too many rows to send). */
export function getCombinedMonth(
  month: string,
  branchIds: readonly string[],
  filters: CalendarFilters,
  signal?: AbortSignal,
): Promise<CombinedMonth> {
  const query = new URLSearchParams({ month, branchIds: branchIds.join(",") });
  const lists: [string, readonly string[]][] = [
    ["hideBranches", filters.branchIds],
    ["hideProviders", filters.providerIds],
    ["hideChairs", filters.chairIds],
    ["hideTypes", filters.types],
    ["hideStatuses", filters.statuses],
  ];
  for (const [key, values] of lists) if (values.length > 0) query.set(key, values.join(","));
  if (filters.onlyProviderId !== null) query.set("onlyProvider", filters.onlyProviderId);
  return api<CombinedMonth>(`/calendar/combined/month?${query.toString()}`, { signal });
}

/** The locations this person shows, remembered on their account (CF-D12), and what they may pick. */
export function getCalendarSelection(signal?: AbortSignal): Promise<CalendarSelection> {
  return api<CalendarSelection>("/users/me/calendar-locations", { signal });
}

export function saveCalendarSelection(branchIds: readonly string[]): Promise<CalendarSelection> {
  return api<CalendarSelection>("/users/me/calendar-locations", {
    method: "PUT",
    body: { branchIds },
  });
}
